import { forwardRef, useCallback, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/lib/hooks/useMediaQuery';
import { typography } from '@/styles/typography';
import type { SubcategoryOption } from '@/types/service-directory';
import {
  _broadcastTooltipShow,
  _listeners,
} from '@/components/base/Tooltip/Tooltip';

/**
 * Tooltip portal component - renders tooltip in document body to avoid overflow clipping
 *
 * On mobile (< 800px): horizontally centered on the viewport to prevent overflow.
 * On tablet/desktop: centered on the pill element, clamped to viewport bounds
 * with 16px padding to prevent overflow when pills are near screen edges.
 */
function TooltipPortal({
  containerRef,
  disabledTooltip,
  isMobile,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  disabledTooltip: string;
  isMobile: boolean;
}) {
  const [style, setStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const gap = 8;

    if (isMobile) {
      // Mobile: open below if trigger is in top half, above if in bottom half
      const viewportHeight = window.innerHeight;
      const triggerMidpoint = (rect.top + rect.bottom) / 2;
      const inTopHalf = triggerMidpoint < viewportHeight / 2;

      const verticalStyle: React.CSSProperties = inTopHalf
        ? { top: rect.bottom + gap }
        : { bottom: viewportHeight - rect.top + gap };

      setStyle({
        ...verticalStyle,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100vw - 88px)',
        maxWidth: '340px',
        boxShadow: '0px 4px 4px 0px #BBBBBB40',
      });
    } else {
      // Tablet/Desktop: centered on the pill, clamped to viewport
      const tooltipWidth = 303;
      const padding = 16;
      let left = rect.left + rect.width / 2;

      // Clamp so tooltip stays within viewport (left is the center point,
      // CSS translateX(-50%) shifts it left by half the width)
      const minLeft = padding + tooltipWidth / 2;
      const maxLeft = window.innerWidth - padding - tooltipWidth / 2;
      left = Math.max(minLeft, Math.min(maxLeft, left));

      setStyle({
        top: rect.top - gap,
        left,
        boxShadow: '0px 4px 4px 0px #BBBBBB40',
      });
    }
  }, [containerRef, isMobile]);

  return createPortal(
    <div
      role="tooltip"
      className={cn(
        'fixed z-[200]',
        !isMobile && '-translate-x-1/2 -translate-y-full',
        !isMobile && 'w-[303px]',
        'bg-white',
        'border border-[#EBEBEB]',
        'rounded-[4px]',
        'p-[8px_16px]',
        typography.body,
        'text-foreground'
      )}
      style={style}
    >
      {disabledTooltip}
    </div>,
    document.body
  );
}

/**
 * Props interface for SubcategoryPill component
 */
export interface SubcategoryPillProps {
  /** Subcategory option data */
  subcategory: SubcategoryOption;
  /** Whether the pill is selected */
  isSelected: boolean;
  /** Whether the pill is disabled */
  isDisabled?: boolean;
  /** Tooltip text to show when disabled (on hover/tap) */
  disabledTooltip?: string;
  /** Callback when pill is clicked */
  onClick: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Default tooltip text for disabled pills
 */
const DEFAULT_DISABLED_TOOLTIP =
  'No relevant services found in this area. Try expanding your search range.';

// Module-level counter shared with Tooltip's singleton system
let pillTooltipIdCounter = 0;

/**
 * SubcategoryPill - Interactive pill component for subcategory selection
 *
 * Displays a subcategory as a selectable pill with multiple visual states:
 * - Default: White background with input border
 * - Hover: Primary brand hover background and text
 * - Selected: Primary brand border with checkmark icon
 * - Disabled: Muted background and text, shows tooltip on hover/tap
 *
 * Integrates with the shared Tooltip singleton manager so that only one
 * tooltip is visible across the entire app (including both Tooltip and
 * SubcategoryPill tooltips).
 *
 * @example
 * ```tsx
 * import { SubcategoryPill } from '@/components';
 *
 * <SubcategoryPill
 *   subcategory={{ id: 'meals', name: 'Home-delivered meals', categoryId: 'meal-services' }}
 *   isSelected={true}
 *   onClick={() => handleToggle('meals')}
 * />
 * ```
 */
export const SubcategoryPill = forwardRef<HTMLButtonElement, SubcategoryPillProps>(
  (
    {
      subcategory,
      isSelected,
      isDisabled = false,
      disabledTooltip = DEFAULT_DISABLED_TOOLTIP,
      onClick,
      className,
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const instanceIdRef = useRef<string>('');
    const mousePositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    // Assign unique ID for singleton coordination
    useEffect(() => {
      pillTooltipIdCounter += 1;
      instanceIdRef.current = `pill-tooltip-${pillTooltipIdCounter}`;
    }, []);

    // Clean up timeout on unmount
    useEffect(() => {
      return () => {
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }
      };
    }, []);

    // Singleton: listen for other tooltips showing
    useEffect(() => {
      const handler = (activeId: string) => {
        if (activeId !== instanceIdRef.current) {
          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
            tooltipTimeoutRef.current = null;
          }
          setShowTooltip(false);
        }
      };

      _listeners.add(handler);
      return () => {
        _listeners.delete(handler);
      };
    }, []);

    // Scroll dismiss: hide tooltip when trigger scrolls out from under cursor
    useEffect(() => {
      if (!showTooltip) return;

      const handleScroll = () => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const { x, y } = mousePositionRef.current;

        const isMouseOverTrigger =
          x >= rect.left &&
          x <= rect.right &&
          y >= rect.top &&
          y <= rect.bottom;

        if (!isMouseOverTrigger) {
          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
            tooltipTimeoutRef.current = null;
          }
          setShowTooltip(false);
        }
      };

      window.addEventListener('scroll', handleScroll, {
        passive: true,
        capture: true,
      });

      return () => {
        window.removeEventListener('scroll', handleScroll, { capture: true });
      };
    }, [showTooltip]);

    // Close tooltip when clicking outside
    useEffect(() => {
      if (!showTooltip) return;

      const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setShowTooltip(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, [showTooltip]);

    const handleClick = useCallback(() => {
      if (isDisabled) {
        // On tap (mobile/tablet), toggle tooltip
        setShowTooltip((prev) => {
          const next = !prev;
          if (next) {
            _broadcastTooltipShow(instanceIdRef.current);
          }
          return next;
        });
      } else {
        onClick();
      }
    }, [isDisabled, onClick]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if ((event.key === 'Enter' || event.key === ' ') && !isDisabled) {
          event.preventDefault();
          onClick();
        }
      },
      [isDisabled, onClick]
    );

    const handleMouseEnter = useCallback(
      (event: React.MouseEvent) => {
        mousePositionRef.current = { x: event.clientX, y: event.clientY };
        if (isDisabled) {
          // Clear any existing timeout
          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
          }
          setShowTooltip(true);
          _broadcastTooltipShow(instanceIdRef.current);
        }
      },
      [isDisabled]
    );

    const handleMouseMove = useCallback((event: React.MouseEvent) => {
      mousePositionRef.current = { x: event.clientX, y: event.clientY };
    }, []);

    const handleMouseLeave = useCallback(() => {
      if (isDisabled) {
        // Small delay before hiding to prevent flickering
        tooltipTimeoutRef.current = setTimeout(() => {
          setShowTooltip(false);
        }, 100);
      }
    }, [isDisabled]);

    return (
      <div ref={containerRef} className="relative inline-block max-w-full">
        <button
          ref={ref}
          type="button"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onMouseEnter={handleMouseEnter}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          disabled={false} // Keep button clickable for tooltip on mobile
          aria-pressed={isSelected}
          aria-disabled={isDisabled}
          className={cn(
            // Base styles
            'inline-flex items-center gap-1',
            'h-8',
            'pt-1 pr-4 pb-1 pl-4',
            'rounded-full',
            'transition-colors duration-150',
            'motion-reduce:transition-none',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2',
            'cursor-pointer',
            'max-w-full',
            // State-based typography
            !isSelected && !isDisabled && typography.body,
            isSelected && !isDisabled && typography.buttonDefault,
            isDisabled && typography.body,
            // State-based styles
            {
              // Default (unselected) state
              'bg-white border border-[var(--color-input-border)] text-foreground':
                !isSelected && !isDisabled,
              // Hover state for unselected
              'hover:border-[var(--color-primary-brand-hover)] hover:text-[var(--color-primary-brand-hover)] hover:bg-[var(--color-bg-hover)]':
                !isSelected && !isDisabled,
              // Selected state
              'bg-[var(--color-pill-selected-bg)] border border-[var(--color-pill-selected-border)] text-[var(--color-primary-brand)]':
                isSelected && !isDisabled,
              // Disabled state
              'bg-[var(--color-pill-disabled-bg)] border border-[var(--color-pill-disabled-border)] text-[var(--color-pill-disabled-text)] cursor-not-allowed':
                isDisabled,
            },
            className
          )}
        >
          {isSelected && !isDisabled && (
            <Check
              className="h-4 w-4 text-[var(--color-primary-brand)] shrink-0"
              aria-hidden="true"
            />
          )}
          <span className="truncate">{subcategory.name}</span>
        </button>

        {/* Tooltip for disabled state - rendered via portal to avoid overflow clipping */}
        {isDisabled && showTooltip && (
          <TooltipPortal
            containerRef={containerRef}
            disabledTooltip={disabledTooltip}
            isMobile={isMobile}
          />
        )}
      </div>
    );
  }
);

SubcategoryPill.displayName = 'SubcategoryPill';
